import { isAppBusy, reloadWhenSafe } from '@core/app-version/reload-when-safe.js';
import { startVersionCheck } from '@core/app-version/version-check.js';
import type { RouterEvents } from '@tanstack/react-router';
import { toast } from 'sonner';

import { t } from '../translations.js';
import { NewVersionToast } from './new-version-toast.js';

export const NEW_VERSION_TOAST_ID = 'new-version';

/** The one router capability this module needs; `router.subscribe` satisfies it. */
type NavigationSource = {
  subscribe: (
    eventType: 'onBeforeNavigate',
    listener: (event: RouterEvents['onBeforeNavigate']) => void,
  ) => () => void;
};

export type NewVersionHandlingOptions = {
  router: NavigationSource;
  /** This bundle's build id (`VITE_SENTRY_RELEASE`). Absent → the check stays off. */
  buildId: string | undefined;
  reload?: () => void;
  navigate?: (href: string) => void;
  fetchBuildId?: () => Promise<string | null>;
};

/**
 * Proactive version-skew handling: poll for a newer deploy and, once one is
 * seen, get the tab onto it before a stale chunk can fail.
 *
 * - A persistent toast offers a reload; its action waits for pending saves
 *   (`reloadWhenSafe`).
 * - The next in-SPA route change while nothing is saving becomes a hard
 *   navigation to the same destination, so the tab picks up the new build
 *   without the user noticing.
 */
export function registerNewVersionHandling({
  router,
  buildId,
  reload = () => {
    window.location.reload();
  },
  navigate = (href) => {
    window.location.assign(href);
  },
  fetchBuildId,
}: NewVersionHandlingOptions): () => void {
  if (!buildId) return () => {};

  let newVersionAvailable = false;

  const stopCheck = startVersionCheck({
    currentBuildId: buildId,
    ...(fetchBuildId ? { fetchBuildId } : {}),
    onNewVersion: () => {
      newVersionAvailable = true;
      toast(t.app.newVersion.toastTitle, {
        id: NEW_VERSION_TOAST_ID,
        description: <NewVersionToast onReload={() => reloadWhenSafe({ reload })} />,
        duration: Infinity,
      });
    },
  });

  const unsubscribe = router.subscribe(
    'onBeforeNavigate',
    ({ toLocation, pathChanged }) => {
      if (!newVersionAvailable || !pathChanged || isAppBusy()) return;
      navigate(toLocation.href);
    },
  );

  return () => {
    stopCheck();
    unsubscribe();
  };
}
